import { useScrollReveal } from '../hooks/useScrollReveal';
import { useLang } from '../i18n/LanguageContext';
import styles from './About.module.css';

const STATS = [
  { value: '20+', label: { en: 'Years of experience', de: 'Jahre Erfahrung' } },
  { value: '1,000+', label: { en: 'Clips delivered to clients', de: 'Clips an Kund:innen geliefert' } },
  { value: '18', label: { en: 'Countries served', de: 'Länder betreut' } },
];

export default function About() {
  const revealRef = useScrollReveal();
  const { t, tr, lang } = useLang();

  return (
    <section id="about" className={styles.section}>
      <div className={`container reveal ${styles.inner}`} ref={revealRef}>
        <p className="section-label">{t('about.eyebrow')}</p>
        <h2 className={styles.headline}>{tr('Built for live events.', 'Gemacht für Live-Events.')}</h2>

        <div className={styles.columns}>
          <div className={styles.copy}>
            <p>
              {tr(
                <>
                  The reason our clients stay with us is that we show up, deliver a great product on
                  time, and make your event feel easier instead of harder. Apparently that&apos;s rarer
                  than it should be.
                </>,
                <>
                  Der Grund, warum unsere Kund:innen bei uns bleiben, ist, dass wir auftauchen, ein
                  großartiges Produkt pünktlich liefern und Ihr Event leichter statt schwerer
                  wirken lassen. Offenbar ist das seltener, als es sein sollte.
                </>,
              )}
            </p>
            <p>
              {tr(
                <>
                  I&apos;m Amit Edelman, based in Berlin. In twenty years behind the camera I&apos;ve
                  filmed news between Molotov cocktails, shot a feature film and documentaries for
                  cinema, and handled camera control and color on multi-million-dollar TV productions.
                  Along the way I&apos;ve worn every hat there is: director, cinematographer, editor,
                  and when the job needed it, sound.
                </>,
                <>
                  Ich bin Amit Edelman, ansässig in Berlin. In zwanzig Jahren hinter der Kamera habe
                  ich News zwischen Molotow-Cocktails gefilmt, einen Kinofilm und Dokumentationen für
                  das Kinema gedreht und Camera Control und Color bei mehrstufigen TV-Produktionen
                  übernommen. Dabei habe ich jeden Hut getragen, den es gibt: Regie, Kamera, Schnitt —
                  und wenn es nötig war, auch Ton.
                </>,
              )}
            </p>
            <p>
              {tr(
                <>
                  These days my focus is live events: over a thousand clips delivered at medical
                  congresses, tech summits, and brand events across Europe and beyond.
                </>,
                <>
                  Heute liegt mein Fokus auf Live-Events: über tausend Clips geliefert bei
                  medizinischen Kongressen, Tech-Summits und Marken-Events in ganz Europa und darüber
                  hinaus.
                </>,
              )}
            </p>
            <p>
              {tr(
                <>
                  What I&apos;ve learned is that the camera is only part of the job. Live events move
                  fast, people are under pressure, and plans change while you&apos;re already rolling.
                  You need to read the room, understand what the team actually needs, and stay calm
                  enough to do your job properly while Godzilla stomps through the exhibition hall
                  and steps on the water dispenser.
                </>,
                <>
                  Was ich gelernt habe: Die Kamera ist nur ein Teil des Jobs. Live-Events bewegen
                  sich schnell, Menschen stehen unter Druck, und Pläne ändern sich, während Sie
                  schon auf Record sind. Man muss den Raum lesen, verstehen, was das Team wirklich
                  braucht, und ruhig genug bleiben, um den Job richtig zu machen — während Godzilla
                  durch die Messehalle stampft und auf den Wasserspender tritt.
                </>,
              )}
            </p>
            <p>
              {tr(
                <>
                  Some productions I wear all those hats at once. On others, I coordinate trusted
                  colleagues to reach the same goal: a happy client and a product worthy of the
                  effort. Either way, you get one point of contact: no handoffs, no dropped balls,
                  no surprises.
                </>,
                <>
                  Bei manchen Produktionen trage ich all diese Hüte zugleich. Bei anderen koordiniere
                  ich vertraute Kolleg:innen, um dasselbe Ziel zu erreichen: eine:n glückliche:n
                  Kund:in und ein Produkt, das die Mühe wert ist. So oder so haben Sie einen
                  Ansprechpartner: keine Übergaben, keine verlorenen Bälle, keine Überraschungen.
                </>,
              )}
            </p>
            <p>{tr('If you care about the result, so do we.', 'Wenn Sie am Ergebnis liegen, tun wir das auch.')}</p>
            <p className={styles.tagline}>{tr('Berlin-based. On the road often.', 'Berlin-basiert. Oft unterwegs.')}</p>
          </div>

          <div className={styles.photoWrap}>
            <div className={styles.photo}>
              <img
                src="/images/amit-on-set.png"
                alt={tr(
                  'Amit Edelman operating a cinema camera on set',
                  'Amit Edelman bedient eine Kinokamera am Set',
                )}
                className={styles.photoImg}
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
        </div>

        <div className={styles.stats}>
          {STATS.map((stat) => (
            <div key={stat.label.en} className={styles.stat}>
              <span className={styles.statValue}>{stat.value}</span>
              <span className={styles.statLabel}>{stat.label[lang] ?? stat.label.en}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
